/**
 * TrophyChest — el "cofre" de trofeos del estudiante.
 *
 * Botón (copa) hermano visual de InfoButton/ContentsButton + panel galería
 * estilo videojuego: los trofeos desbloqueados a color y los bloqueados en
 * silueta con su progreso ("2/3") y una pista de cómo lograrlos.
 *
 * Nombres de trofeos en FRANCÉS (el logro celebra un acto en francés);
 * explicaciones/pistas en español. Solo estudiantes.
 *
 * Desde la mesa del 29/09/2026 (Santiago, Greg, Carlos):
 *  - pestañas por nivel (A1 … hasta el nivel actual del estudiante) y una
 *    pestaña « Tous »: lo ganado en niveles anteriores sigue visible (memoria
 *    de trofeos — la persistencia siempre fue por id de trofeo, que lleva el
 *    nivel; lo que faltaba era poder volver a verlo);
 *  - el título dice el nivel (« Mes trophées A2 »);
 *  - las categorías de la leyenda filtran (lingüística, comunicación,
 *    especial, académica) dentro de la pestaña — con « Tous » filtran todos
 *    los niveles;
 *  - los trofeos académicos (notas y asistencia AEC) se agrupan por el nivel
 *    del curso en que se ganaron (galia-ws manda el nivel con cada casilla).
 */

import { useEffect, useMemo, useState } from 'react';
import { TrophyMedal } from './TrophyMedal';
import { TROPHY_MEDALS, CATEGORY_LABELS_FR, categoryIconSrc, type MedalCategory } from '../../config/trophyMedals';
import achievementService, { ActionProgress } from '../../services/achievementService';
import { ACTION_ACHIEVEMENTS } from '../../config/actionAchievements';
import {
  AEC_TROPHIES,
  LEVEL_TROPHIES,
  LevelTrophy,
  isTrophyComplete,
  trophiesForLevel,
  trophyProgress,
  trophyTarget,
} from '../../config/trophies';
import { MCERLevel } from '../../types';
import { isKioskMode } from '../../utils/featureFlags';
import './InfoButton.css'; // reusa .info-button para el estilo base del botón
import './TrophyChest.css';

/* ── Botón (junto a los demás controles) ── */

interface TrophyChestButtonProps {
  onClick: () => void;
  isOpen?: boolean;
  /** Nº de trofeos NUEVOS (sin ver) desde la última apertura del cofre:
   *  muestra un puntito con el conteo; al abrir el cofre se apaga. */
  unlockedCount?: number;
}

export function TrophyChestButton({ onClick, isOpen = false, unlockedCount = 0 }: TrophyChestButtonProps) {
  return (
    <button
      type="button"
      className={`info-button trophy-chest-button ${isOpen ? 'hidden' : ''}`}
      onClick={onClick}
      aria-label="Mis trofeos"
      title="Mis trofeos"
    >
      {/* Copa (trofeo) */}
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M8 21h8" />
        <path d="M12 17v4" />
        <path d="M7 4h10v6a5 5 0 0 1-10 0V4z" />
        <path d="M7 6H4a2 2 0 0 0 2 5h1" />
        <path d="M17 6h3a2 2 0 0 1-2 5h-1" />
      </svg>
      {unlockedCount > 0 && (
        <span className="trophy-chest-badge" aria-hidden="true">{unlockedCount}</span>
      )}
    </button>
  );
}

/* ── Pestañas por nivel ── */

/** Pestañas del cofre: los niveles del Excel (la hoja C sirve C1 y C2). */
type TabLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C';
type Tab = TabLevel | 'all';
const TAB_ORDER: TabLevel[] = ['A1', 'A2', 'B1', 'B2', 'C'];

/** Pestaña a la que pertenece un nivel MCER o un nivel base de curso AEC. */
function tabOfLevel(level?: string | null): TabLevel | null {
  if (!level) return null;
  const base = level.match(/^([ABC][12])/)?.[1];
  if (!base) return null;
  return base.startsWith('C') ? 'C' : (base as TabLevel);
}

/** Catálogo de nivel de una pestaña (C → hoja C, indexada como C1). */
function trophiesOfTab(tab: TabLevel): LevelTrophy[] {
  return LEVEL_TROPHIES[(tab === 'C' ? 'C1' : tab) as MCERLevel] || [];
}

/* ── Panel galería ── */

interface TrophyChestPanelProps {
  email?: string | null;
  /** Nivel MCER del estudiante: si su hoja del Excel de trofeos ya está
   *  implementada (A1 por ahora), el cofre muestra ESOS trofeos; si no,
   *  cae al catálogo legacy por regex. */
  level?: MCERLevel;
  onClose: () => void;
}

interface Card {
  id: string;
  emoji: string;
  titleFr: string;
  blurb: string;
  hint: string;
  count: number;
  threshold: number;
  unlocked: boolean;
  category?: MedalCategory;
}

export function TrophyChestPanel({ email, level, onClose }: TrophyChestPanelProps) {
  const [unlocked, setUnlocked] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<ActionProgress>({ counts: {}, unlocked: [] });
  const currentTab = tabOfLevel(level);
  const [tab, setTab] = useState<Tab>(currentTab || 'all');
  const [category, setCategory] = useState<MedalCategory | 'all'>('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await achievementService.getUnlockedAchievements(email);
        const p = email
          ? await achievementService.getProgress(email)
          : { counts: {}, unlocked: [] };
        if (cancelled) return;
        setUnlocked(u);
        setProgress(p);
      } catch (err) {
        console.error('[TrophyChest] Error al cargar trofeos:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email]);

  const kiosk = isKioskMode();
  // Catálogo del nivel actual: si está vacío (nivel sin hoja) el cofre cae al
  // catálogo legacy por regex y no hay pestañas.
  const levelTrophies = trophiesForLevel(level);
  const withTabs = levelTrophies.length > 0 && !kiosk && currentTab !== null;
  // Pestañas visibles: de A1 hasta el nivel actual del estudiante.
  const tabs: TabLevel[] = withTabs
    ? TAB_ORDER.slice(0, TAB_ORDER.indexOf(currentTab as TabLevel) + 1)
    : [];
  const activeTab: Tab = withTabs ? (tab === 'all' || tabs.includes(tab as TabLevel) ? tab : currentTab!) : 'all';

  const trophyCard = (t: LevelTrophy, siblings: LevelTrophy[], counts?: Record<string, number>, isUnlocked?: boolean): Card => ({
    id: t.id,
    emoji: t.emoji,
    titleFr: t.titleFr,
    // El estudiante lee la COLUMNA F del Excel (descFr) en TODAS las tarjetas,
    // bloqueadas o no (Santiago, revisión del 09/09: "la columna F debe
    // reemplazar esa descripción"; el 26/08 las bloqueadas mostraban la pista
    // en español y él lo vio como "trofeos en español"). La columna F está en
    // pasado, pero es el texto que la AF quiere en la interfaz. La pista en
    // español (hint) queda solo para el kiosco, donde sí es instructiva.
    blurb: t.descFr,
    hint: t.hint,
    count: t.meta
      ? siblings.filter((x) => !x.meta && unlocked.has(x.id)).length
      : trophyProgress(t, counts),
    threshold: t.meta
      ? siblings.filter((x) => !x.meta).length
      : Math.max(trophyTarget(t), 1),
    unlocked: isUnlocked ?? unlocked.has(t.id),
    category: TROPHY_MEDALS[t.id]?.category,
  });

  /**
   * Casillas de un trofeo académico que pertenecen a una pestaña. La casilla
   * es el id del curso y su nivel viene en progress.aecLevels; las casillas
   * sin nivel (anteriores al 30/09/2026) cuentan en la pestaña del nivel actual.
   */
  const academicCounts = (t: LevelTrophy, forTab: Tab): Record<string, number> => {
    const all = progress.trophyCounts?.[t.id] || {};
    if (forTab === 'all') return all;
    const out: Record<string, number> = {};
    for (const [slot, n] of Object.entries(all)) {
      const slotTab = tabOfLevel(progress.aecLevels?.[slot]) || currentTab;
      if (slotTab === forTab) out[slot] = n;
    }
    return out;
  };

  const { cards, academicCards } = useMemo((): { cards: Card[]; academicCards: Card[] } => {
    if (levelTrophies.length === 0) {
      // Catálogo legacy por regex (nivel sin hoja en el Excel).
      return {
        cards: ACTION_ACHIEVEMENTS.map((a) => ({
          id: a.id,
          emoji: a.emoji,
          titleFr: a.titleFr,
          blurb: a.blurb,
          hint: a.hint,
          count: Math.min(progress.counts[a.id] || 0, a.threshold),
          threshold: a.threshold,
          unlocked: unlocked.has(a.id),
        })),
        academicCards: [],
      };
    }
    if (!withTabs) {
      // Kiosco: los 3 trofeos de recepción, sin académicos (el visitante es anónimo).
      return {
        cards: levelTrophies.map((t) => trophyCard(t, levelTrophies, progress.trophyCounts?.[t.id])),
        academicCards: [],
      };
    }
    const levelsToShow: TabLevel[] = activeTab === 'all' ? tabs : [activeTab as TabLevel];
    const levelCards = levelsToShow.flatMap((lv) => {
      const list = trophiesOfTab(lv);
      return list.map((t) => trophyCard(t, list, progress.trophyCounts?.[t.id]));
    });
    // Trofeos académicos (notas y asistencia AEC) del nivel de la pestaña. En
    // « Tous » vale el estado global; por nivel se recalcula con las casillas
    // (cursos) de ese nivel.
    const academic = AEC_TROPHIES.map((t) => {
      const counts = academicCounts(t, activeTab);
      const done = activeTab === 'all' ? unlocked.has(t.id) || isTrophyComplete(t, counts) : isTrophyComplete(t, counts);
      return trophyCard(t, AEC_TROPHIES, counts, done);
    });
    return { cards: levelCards, academicCards: academic };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelTrophies, withTabs, activeTab, tabs.join(','), progress, unlocked]);

  // Categorías presentes (leyenda = filtro). Orden fijo.
  const LEGEND_ORDER: MedalCategory[] = ['linguistica', 'comunicacion', 'especial', 'academica'];
  const presentes = new Set([...cards, ...academicCards].map((c) => c.category).filter(Boolean));
  const legendCategories = LEGEND_ORDER.filter((c) => presentes.has(c));
  const activeCategory: MedalCategory | 'all' =
    category === 'all' || legendCategories.includes(category) ? category : 'all';
  const byCategory = (c: Card) => activeCategory === 'all' || c.category === activeCategory;
  const shownCards = cards.filter(byCategory);
  const shownAcademic = academicCards.filter(byCategory);

  const total = cards.length + academicCards.length;
  const done = cards.filter((c) => c.unlocked).length + academicCards.filter((c) => c.unlocked).length;
  const doneInTab = (lv: TabLevel): string => {
    const list = trophiesOfTab(lv);
    const n = list.filter((t) => unlocked.has(t.id)).length;
    return `${n}/${list.length}`;
  };
  const titleLevel = withTabs && activeTab !== 'all' ? ` ${activeTab}` : '';

  const renderCard = (a: Card) => (
    <div key={a.id} className={`trophy-card ${a.unlocked ? 'unlocked' : 'locked'}`}>
      <TrophyMedal trophyId={a.id} emoji={a.emoji} className="trophy-card-emoji" />
      <span className="trophy-card-title">{a.titleFr}</span>
      {a.unlocked ? (
        <span className="trophy-card-blurb">{a.blurb}</span>
      ) : (
        <>
          <span className="trophy-card-blurb">{kiosk ? a.hint : a.blurb}</span>
          <span className="trophy-card-progress">
            <span
              className="trophy-card-progress-bar"
              style={{ width: `${(a.count / a.threshold) * 100}%` }}
            />
            <span className="trophy-card-progress-text">
              {a.count}/{a.threshold}
            </span>
          </span>
        </>
      )}
    </div>
  );

  return (
    <div className="trophy-chest-overlay" onClick={onClose} role="dialog" aria-label="Mis trofeos">
      <div className="trophy-chest-panel" onClick={(e) => e.stopPropagation()}>
        <div className="trophy-chest-header">
          <div className="trophy-chest-heading">
            <h2 className="trophy-chest-title">Mes trophées{titleLevel} 🏆</h2>
            <span className="trophy-chest-subtitle">
              Mis trofeos — {done}/{total} desbloqueados
            </span>
          </div>
          <button
            type="button"
            className="trophy-chest-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Pestañas por nivel: A1 … nivel actual, y « Tous » (todos los niveles). */}
        {withTabs && (
          <ul className="trophy-chest-tabs" role="tablist" aria-label="Niveaux">
            {tabs.map((lv) => (
              <li key={lv} role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === lv}
                  className={`trophy-chest-tab ${activeTab === lv ? 'active' : ''}`}
                  onClick={() => setTab(lv)}
                >
                  {lv}
                  <span className="trophy-chest-tab-count">{doneInTab(lv)}</span>
                </button>
              </li>
            ))}
            {tabs.length > 1 && (
              <li role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'all'}
                  className={`trophy-chest-tab ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setTab('all')}
                >
                  Tous
                </button>
              </li>
            )}
          </ul>
        )}

        {/* Leyenda de categorías: la medalla con Galia en la insignia es el
            «ícono de cada categoría» (Santiago, 18/09/2026). Solo las
            categorías que aparecen en este cofre. Desde el 29/09/2026 también
            filtra (Santiago: « filtro por categoría de todos los niveles »). */}
        {legendCategories.length > 0 && (
          <ul className="trophy-chest-legend" aria-label="Catégories de trophées">
            <li className="trophy-chest-legend-item">
              <button
                type="button"
                className={`trophy-chest-chip trophy-chest-chip-all ${activeCategory === 'all' ? 'active' : ''}`}
                aria-pressed={activeCategory === 'all'}
                onClick={() => setCategory('all')}
              >
                Toutes
              </button>
            </li>
            {legendCategories.map((c) => (
              <li key={c} className="trophy-chest-legend-item">
                <button
                  type="button"
                  className={`trophy-chest-chip ${activeCategory === c ? 'active' : ''}`}
                  aria-pressed={activeCategory === c}
                  onClick={() => setCategory(activeCategory === c ? 'all' : c)}
                >
                  <img src={categoryIconSrc(c)} alt="" className="trophy-chest-legend-icon" draggable={false} />
                  <span>{CATEGORY_LABELS_FR[c]}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {shownCards.length > 0 && (
          <>
            {academicCards.length > 0 && (
              <p className="trophy-chest-section">
                {activeTab === 'all' ? 'Trophées de niveau' : `Trophées du niveau ${activeTab}`}
              </p>
            )}
            <div className="trophy-chest-grid">{shownCards.map(renderCard)}</div>
          </>
        )}

        {shownAcademic.length > 0 && (
          <>
            <p className="trophy-chest-section">
              Trophées académiques 🎓{activeTab !== 'all' ? ` — cours ${activeTab}` : ''}
            </p>
            <div className="trophy-chest-grid">{shownAcademic.map(renderCard)}</div>
          </>
        )}

        {shownCards.length === 0 && shownAcademic.length === 0 && (
          <p className="trophy-chest-empty">Aucun trophée dans cette catégorie.</p>
        )}

        <p className="trophy-chest-footer">
          Continue en français pour tout débloquer ! ✨
        </p>
      </div>
    </div>
  );
}

export default TrophyChestPanel;
